import { ApiError, getHealth, isHandlerUnknownRoute } from '../api';
import { useAsync } from '../lib/async';
import { Facts, Result } from '../lib/ui';

/**
 * What this console can prove about itself, and what it cannot.
 *
 * `/admin/health` is an echo and nothing more: it proves the staff surface, the
 * audience and the `staff` role on this token all line up, and that a token
 * minted for the family surface is rejected rather than silently accepted. It
 * reads no table, so it cannot tell you the pipeline is healthy — the Overview
 * does that from real data.
 *
 * WHAT THIS SCREEN SAID UNTIL NOW, AND WHY IT WAS THE WORST POSSIBLE PLACE FOR
 * IT. `azure/functions/function_app.py` registers `GET control/admin/health` and
 * points it at `api.admin` — but `api/admin.py`'s handler has no branch for
 * `/admin/health`, so the request falls through to its catch-all and the live
 * deployment answers `404 {"error": "unknown admin route"}`. `isMissingRoute`
 * says true for that, so `Result` rendered `missing`, and `missing` read: "If it
 * is missing, the staff API is not deployed." On the screen an operator opens to
 * ask "is it up?", while every other tab in the console was loading real rows
 * out of that same API two clicks away. A console that answers a true question
 * with a false alarm is worse than one with no health page at all.
 *
 * So the 404 is caught here rather than triaged upstream, because on THIS screen
 * the shape of the 404 is the finding. See `isHandlerUnknownRoute` in api.ts:
 * the handler's own body is proof the request was routed, authorized, and
 * carried the `staff` role — auth.py refuses before any handler runs — and the
 * only thing actually missing is the echo. A bodyless host 404 is the other
 * shape and still falls through to `missing` below, because that one really can
 * mean the staff API is not there.
 *
 * The rest of this screen is deliberately a list of things NOT checked. A health
 * page that shows one green tick invites the reading "everything is fine", which
 * is the opposite of true here: the biggest risk in this system is a control
 * plane with production spend behind a single password, and no endpoint reports
 * that. Stating it as an unchecked fact is the honest rendering.
 */
export function Health() {
  const { result, reload } = useAsync(async () => {
    try {
      return { echoed: true as const, health: await getHealth() };
    } catch (err) {
      if (err instanceof ApiError && isHandlerUnknownRoute(err)) {
        return { echoed: false as const };
      }
      throw err;
    }
  }, []);

  return (
    <>
      <h2 style={{ fontSize: 15, fontWeight: 500 }}>This console</h2>
      <Result
        of={result}
        what="the staff health check"
        retry={reload}
        missing="Nothing answered on the staff API's own hostname — not even a refusal. /control/admin/health was not routed at all, which is what a staff API that is not deployed looks like."
      >
        {(probe) =>
          !probe.echoed ? (
            <div className="notice">
              <b>The staff API answered, and it has no health echo.</b>
              <span className="muted small">
                <span className="mono">GET /control/admin/health</span> returned{' '}
                <span className="mono">404 {'{"error": "unknown admin route"}'}</span>
                {' — '}
                that body is written by <span className="mono">api/admin.py</span>{' '}
                itself, so the request WAS routed and WAS authorized. It is not
                evidence of an outage; it is a route the handler never
                implemented.
              </span>
              <span className="muted small">
                Reaching that catch-all means four things held:{' '}
                <span className="mono">/control</span> is on the Function App, the
                token's audience matched STAFF_AUDIENCE, the <span className="mono">staff</span>{' '}
                app role was present, and the module imported.{' '}
                <span className="mono">azure/functions/auth.py</span> answers 401
                or 403 before any handler runs, so none of those could have failed
                and still produced this. What is unproven is only what the echo
                itself would have added: nothing.
              </span>
            </div>
          ) : (
            <Facts
              rows={[
                ['Staff API', probe.health.ok ? 'answering' : 'answered without ok:true'],
                ['Surface', probe.health.surface ?? ''],
                // Echoed back from the token's own claims, so it is proof the
                // STAFF surface authorized this call — right audience, and the
                // `staff` role present. It is the `oid`, not an email: Entra's
                // `sub` is pairwise per application and would name the same human
                // differently here and in the iOS app.
                ['Signed in as', probe.health.staff ?? ''],
              ]}
            />
          )
        }
      </Result>

      <div className="notice notice-warn" style={{ marginTop: 18 }}>
        <b>Whether this account has MFA is not visible from here.</b>
        <span className="muted small">
          This said "the staff pool has no MFA" until 2026-08-22, quoting{' '}
          <span className="mono">MfaConfiguration: OFF</span> off the CDK. That
          pool is gone. Sign-in is Entra now, where multi-factor is a Conditional
          Access policy on the tenant rather than a property of a user pool — so
          the honest statement is that it could be enforced or not and this page
          would look identical either way. Nothing in the staff API reports it,
          and inventing a green tick for it would be worse than saying so.
        </span>
        <span className="muted small">
          What IS enforced, and is checkable: the staff app registration requires
          role assignment, so an account without the{' '}
          <span className="mono">staff</span> role cannot obtain a token for this
          API at all. Whatever the box above says, it says it because that role
          check passed — a refusal would have replaced this whole screen with the
          sentence naming <span className="mono">entra_bootstrap.sh</span>.
        </span>
      </div>

      <h2 style={{ fontSize: 15, fontWeight: 500, marginTop: 26 }}>Not checked here</h2>
      <div className="notice">
        <span className="muted small">
          Each of these is a real gap rather than a passing check, and each would
          need an endpoint that does not exist yet:
        </span>
        <ul className="muted small" style={{ margin: '6px 0 0 18px' }}>
          <li>
            <b>Table reachability.</b> Every browser proves its own table by
            returning rows; there is no aggregate probe. A table that is broken
            shows up as one screen failing, not as a red light here.
          </li>
          <li>
            <b>TTL configuration.</b> <span className="mono">showling-characters</span>{' '}
            and <span className="mono">showling-commissions</span> both write a{' '}
            <span className="mono">ttl</span> attribute, and nothing here reads
            whether the store is configured to expire on it. The claim-row
            toggles on Pipeline and Characters show the consequence — permanent
            idempotency rows — but the cause is unconfirmed from this console.
          </li>
          <li>
            <b>Pipeline visibility — and this is the big one.</b> Films are
            started and do run; what is missing is any way to watch one.
            shared/workflow.py exposes no introspection on the Durable lane, so
            all four <span className="mono">/admin/pipeline</span> routes answer
            503 and the Executions screen says so rather than showing an empty
            list. That leaves the failure text stored on the commission row as
            the whole record of a bad run.
          </li>
          <li>
            <b>Provider reachability.</b> Nothing here calls the gate or the
            video providers, so a provider outage is invisible until commissions
            start failing.
          </li>
        </ul>
      </div>
    </>
  );
}

/* There was a `configurationNote()` helper here, exported and imported by
   nothing, kept on the argument that 501 deserved its own reading. It does —
   and that reading is now `isUnavailable` in api.ts, wired into `Result`, so
   every screen gets it rather than one helper nobody called. */
